import type {
  PhoneMailHighlightItem,
  PhoneMailTriageAction,
  PhoneMailTriageReactionStep,
} from '../types';

/** Legacy scenarios may store plain strings instead of `{ term, field }`. */
export type HighlightInEmailInput = string | PhoneMailHighlightItem;

export function normalizeHighlightInEmail(
  items?: HighlightInEmailInput[],
  { keepEmptyDrafts = false }: { keepEmptyDrafts?: boolean } = {}
): PhoneMailHighlightItem[] | undefined {
  if (!items?.length) return keepEmptyDrafts ? [] : undefined;
  const normalized = items
    .map((item) => {
      if (typeof item === 'string') {
        const term = keepEmptyDrafts ? item : item.trim();
        if (!term && !keepEmptyDrafts) return null;
        return { term, field: 'from' as const };
      }
      const term = keepEmptyDrafts ? (item.term ?? '') : (item.term?.trim() ?? '');
      const field = item.field ?? 'from';
      if (!term && !keepEmptyDrafts) return null;
      return { term, field };
    })
    .filter((item): item is PhoneMailHighlightItem => item !== null);
  if (!normalized.length) return keepEmptyDrafts ? [] : undefined;
  return normalized;
}

/** Flat legacy `reactions` + `highlightInEmail` → ordered reaction steps. */
export function resolveTriageReactionSteps(
  action: PhoneMailTriageAction
): PhoneMailTriageReactionStep[] {
  if (action.reactionSteps?.length) {
    return action.reactionSteps
      .filter((step) => step.reactions?.length)
      .map((step) => ({
        reactions: step.reactions,
        highlightInEmail: normalizeHighlightInEmail(
          step.highlightInEmail as HighlightInEmailInput[] | undefined
        ),
      }));
  }
  if (!action.reactions?.length) return [];
  return [{
    reactions: action.reactions,
    highlightInEmail: normalizeHighlightInEmail(
      action.highlightInEmail as HighlightInEmailInput[] | undefined
    ),
  }];
}

export function triageStepHasHighlight(step?: PhoneMailTriageReactionStep): boolean {
  return Boolean(step?.highlightInEmail?.length);
}

export function triageActionHasReactions(action: PhoneMailTriageAction): boolean {
  return resolveTriageReactionSteps(action).length > 0;
}
